import { Link, Stack } from '@mui/material'
import React from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import { AWSAccountIdDrawer } from 'components/rbac-settings/teams/AWSAccountIdDrawer'
import { ThirdPartyAccountDrawer } from 'components/rbac-settings/teams/integrations/third-party/ThirdPartyAccountDrawer'
import { isCustomUrl } from 'components/scenarios/create/status-checks/third-party-presets'
import { ObservabilityToolCustomIcon } from 'components/status-checks/common/ObservabilityToolCustomIcon'
import { ObservabilityToolIcon } from 'components/status-checks/common/ObservabilityToolIcon'
import {
  getIsThirdPartyIntegrationEditable,
  type TeamExternalIntegrationRow
} from 'components/status-checks/common/team-integration-table/team-integration-table-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import theme from 'theme'
import {
  ObservabilityToolType,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'

export function TeamIntegrationTableName({
  hasLogoOnIntegration,
  isThirdPartyIntegrationsTable,
  integration,
  savedIntegrations
}: {
  hasLogoOnIntegration: boolean
  isThirdPartyIntegrationsTable?: boolean
  integration: TeamExternalIntegrationRow
  savedIntegrations: TeamExternalIntegration[]
}) {
  const statusChecksUrls = getStatusChecksUrls()

  const isThirdPartyIntegrationEditable =
    getIsThirdPartyIntegrationEditable(integration)

  return (
    <Stack
      data-testid="status-check-authentication-list-row"
      direction="row"
      spacing={2}
      sx={{ alignItems: 'center' }}
    >
      {hasLogoOnIntegration && (
        <RouterLink
          to={statusChecksUrls.editAuthenticationFromTeamExternalIntegration(
            integration
          )}
        >
          {isCustomUrl(integration.url, integration.observabilityToolType) ? (
            <ObservabilityToolCustomIcon />
          ) : (
            <ObservabilityToolIcon integration={integration} />
          )}
        </RouterLink>
      )}
      <Stack>
        {isThirdPartyIntegrationsTable ? (
          <>
            {isThirdPartyIntegrationEditable ? (
              <AWSAccountIdDrawer
                externalIntegrations={savedIntegrations}
                renderButton={({ openDrawer }) => (
                  <Link onClick={openDrawer}>{integration.displayName}</Link>
                )}
                selectedAWSAccount={integration}
              />
            ) : (
              <ThirdPartyAccountDrawer
                externalIntegrations={savedIntegrations}
                mode="read-only"
                selectedIntegration={integration}
              />
            )}
          </>
        ) : (
          <TruncatedTextWithTooltip
            tooltipProps={{ title: integration.displayName }}
            variant="body2"
          >
            <Link
              component={RouterLink}
              data-test-status-checks-authentication-list-row-name={
                integration.displayName
              }
              to={statusChecksUrls.editAuthenticationFromTeamExternalIntegration(
                integration
              )}
            >
              {integration.displayName}
            </Link>
          </TruncatedTextWithTooltip>
        )}

        {/* Don't show the URL for AWS authentications because it is always hardcoded */}
        {integration.observabilityToolType !== ObservabilityToolType.AWS ? (
          <TruncatedTextWithTooltip
            color={theme.colors.steel[1]}
            tooltipProps={{ title: integration.url, placement: 'bottom' }}
            variant="caption"
          >
            {integration.url}
          </TruncatedTextWithTooltip>
        ) : null}
      </Stack>
    </Stack>
  )
}
